import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { INSTAGRAM, PHONE, WA_URL } from "./data";

// --- Official brand icons (SVG) ---
const WhatsAppIcon = ({ size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
    <path d="M20.52 3.48A11.83 11.83 0 0 0 12.03 0C5.45 0 .13 5.32.13 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.65a11.87 11.87 0 0 0 5.7 1.45h.01c6.58 0 11.9-5.32 11.9-11.9 0-3.18-1.24-6.17-3.41-8.42ZM12.03 21.8h-.01a9.87 9.87 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.66-.24-.38a9.87 9.87 0 0 1-1.51-5.25c0-5.46 4.44-9.9 9.91-9.9 2.65 0 5.13 1.03 7 2.9a9.85 9.85 0 0 1 2.9 6.99c0 5.47-4.44 9.91-9.91 9.91Zm5.44-7.42c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.66.15-.2.3-.75.97-.92 1.17-.17.2-.34.22-.63.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.13.3-.34.44-.51.15-.17.2-.29.3-.49.1-.2.05-.37-.02-.52-.07-.15-.66-1.59-.9-2.18-.24-.57-.48-.49-.66-.5l-.56-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47 0 1.46 1.07 2.87 1.22 3.07.15.2 2.1 3.2 5.1 4.49.71.31 1.27.5 1.7.64.71.23 1.36.2 1.88.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35Z"/>
  </svg>
);

// Official Instagram glyph (camera)
const InstagramIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
    <path d="M12 2c2.717 0 3.056.01 4.122.06 1.065.05 1.79.217 2.428.465.66.254 1.216.598 1.772 1.153a4.9 4.9 0 0 1 1.153 1.772c.247.637.415 1.363.465 2.428C21.99 8.944 22 9.283 22 12c0 2.717-.01 3.056-.06 4.122-.05 1.065-.218 1.79-.465 2.428a4.9 4.9 0 0 1-1.153 1.772 4.9 4.9 0 0 1-1.772 1.153c-.637.247-1.363.415-2.428.465-1.066.05-1.405.06-4.122.06s-3.056-.01-4.122-.06c-1.065-.05-1.79-.218-2.428-.465a4.9 4.9 0 0 1-1.772-1.153 4.9 4.9 0 0 1-1.153-1.772c-.248-.637-.415-1.363-.465-2.428C2.01 15.056 2 14.717 2 12c0-2.717.01-3.056.06-4.122.05-1.065.217-1.79.465-2.428a4.9 4.9 0 0 1 1.153-1.772A4.9 4.9 0 0 1 5.45 2.525c.638-.248 1.363-.415 2.428-.465C8.944 2.01 9.283 2 12 2Zm0 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm6.5-.25a1.25 1.25 0 1 0-2.5 0 1.25 1.25 0 0 0 2.5 0ZM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z"/>
  </svg>
);

const PhoneIcon = ({ size = 22 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden>
    <path d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 1.02-.24c1.12.37 2.33.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.4 21 3 13.6 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.24.2 2.45.57 3.57a1 1 0 0 1-.24 1.02l-2.21 2.2Z"/>
  </svg>
);

export default function FloatingButtons() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 240);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Uniform button size across all three buttons.
  const baseBtn =
    "fab h-12 w-12 sm:h-14 sm:w-14 rounded-full flex items-center justify-center shadow-[0_10px_28px_rgba(0,0,0,0.22)]";

  return (
    <div
      data-testid="floating-actions"
      className="fixed z-40 flex flex-col items-end gap-3"
      style={{
        right: "max(1rem, env(safe-area-inset-right))",
        bottom: "max(1rem, env(safe-area-inset-bottom))",
      }}
    >
      <AnimatePresence>
        {visible && (
          <>
            <motion.a
              key="ig"
              initial={{ opacity: 0, y: 16, scale: 0.85 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.85 }}
              transition={{ delay: 0.05, duration: 0.35, ease: [0.2, 0.9, 0.2, 1] }}
              href={INSTAGRAM}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="fab-instagram"
              aria-label="Instagram"
              className={`${baseBtn} text-white`}
              style={{
                background:
                  "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
              }}
            >
              <InstagramIcon size={22} />
            </motion.a>
            <motion.a
              key="call"
              initial={{ opacity: 0, y: 16, scale: 0.85 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.85 }}
              transition={{ delay: 0.08, duration: 0.35, ease: [0.2, 0.9, 0.2, 1] }}
              href={`tel:+${PHONE}`}
              data-testid="fab-call"
              aria-label="Call"
              className={`${baseBtn} bg-[color:var(--forest)] text-[color:var(--paper)]`}
            >
              <PhoneIcon size={22} />
            </motion.a>
          </>
        )}
      </AnimatePresence>

      {/* WhatsApp — always visible */}
      <a
        href={WA_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="fab-whatsapp"
        aria-label="Book on WhatsApp"
        className={`${baseBtn} relative text-white`}
        style={{ backgroundColor: "#25D366" }}
      >
        <span
          className="absolute inset-0 rounded-full animate-ping opacity-25"
          style={{ backgroundColor: "#25D366" }}
        />
        <WhatsAppIcon size={24} />
      </a>
    </div>
  );
}
